import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Navigate } from 'react-router-dom';
import {
  useBuyerExclusivity,
  useBuyerLoi,
  useBuyerQuestions,
  useBuyerRoomAccess,
  useDocumentSearch,
  useRoomDocuments,
  useRoomFunnel,
  useSigningEnvelopes,
} from '@/api/hooks';
import { AgreementSigningPanel } from '@/components/AgreementSigningPanel';
import { BuyerGuide } from '@/components/BuyerGuide';
import { DocumentList } from '@/components/DocumentList';
import { DocumentViewer } from '@/components/DocumentViewer';
import { NewDocumentAlertsToggle } from '@/components/NewDocumentAlertsToggle';
import { FunnelStepper } from '@/components/FunnelStepper';
import { LoiForm } from '@/components/LoiForm';
import { QuestionsPanel } from '@/components/QuestionsPanel';
import {
  buildFolderTree,
  documentsInFolder,
  folderColumnView,
  folderPathNames,
} from '@/lib/folder-tree';
import { loiCard } from '@/lib/loi-card';
import { getViewerIdentity } from '@/lib/viewerIdentity';
import {
  ApiError,
  AskAnswerSummary,
  AskTheRoomPrompt,
  BuyerExclusivityNotice,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  HelpTip,
  DataRoomFolders,
  ResultsTitle,
  SearchBar,
  askAnswerDocuments,
  describePeriodKey,
  hasSearchCriteria,
  noteGuideActivity,
  useAskTheRoom,
  ErrorText,
  LOI_STATUS_LABELS,
  cn,
  LoiStatusChip,
  PublishedQuestionList,
  formatDate,
  loiDocumentContentPath,
} from '@dataroom/shared';
import type { BuyerLoiState, LoiStatus, RoomDocument } from '@dataroom/shared';

/** Build the minimal RoomDocument the view-only DocumentViewer needs to open the
 * buyer's own signed LOI (it reads only id/name/categoryPath). The bytes come
 * from the LOI document path, never the general library (ADR-0029). */
function loiDocument(roomId: string, loi: BuyerLoiState): RoomDocument {
  return {
    id: loi.documentId ?? loi.id,
    roomId,
    name: loi.filename ?? 'Your letter of intent',
    uploadedBy: null,
    source: 'upload',
    status: 'processing',
    categoryPath: [],
    downloadReady: false,
    createdAt: new Date().toISOString(),
  };
}

/** The line under the buyer's LOI status (ADR-0029). */
const LOI_STATUS_MESSAGES: Record<LoiStatus, string> = {
  submitted:
    "Your signed letter of intent has been submitted and is awaiting the seller's response.",
  accepted: 'The seller has accepted your letter of intent. The deal team will be in touch.',
  accepted_signed: 'The seller has accepted your letter of intent. The deal team will be in touch.',
  redlined:
    'The seller has countered your letter of intent. The deal team will share their terms; you can upload a revised signed LOI below.',
  rejected: 'The seller declined your letter of intent.',
  awaiting_buyer_signature: 'This letter of intent was never signed and submitted.',
};

/**
 * The views of the room's contents: its documents, the buyer's own questions,
 * and — only once the buyer can see the CIM — the shared Buyer Questions.
 */
type RoomView = 'documents' | 'questions' | 'buyer-questions';
const ROOM_VIEWS: { id: RoomView; label: string }[] = [
  { id: 'documents', label: 'Documents' },
  { id: 'questions', label: 'Questions' },
  { id: 'buyer-questions', label: 'Buyer Questions' },
];

/** A minimal accessible tab switch (role=tablist; arrow keys move between tabs). */
function RoomViewTabs({
  view,
  views,
  onChange,
}: {
  view: RoomView;
  views: { id: RoomView; label: string }[];
  onChange: (v: RoomView) => void;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const i = views.findIndex((v) => v.id === view);
    const step = event.key === 'ArrowRight' ? 1 : -1;
    const next = views[(i + step + views.length) % views.length];
    if (!next) return;
    onChange(next.id);
    refs.current[views.indexOf(next)]?.focus();
  };
  return (
    <div className="flex items-center gap-1 border-b">
      <div role="tablist" aria-label="Room views" className="flex gap-1" onKeyDown={onKeyDown}>
        {views.map((v, i) => {
          const selected = v.id === view;
          return (
            <button
              key={v.id}
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              role="tab"
              id={`room-tab-${v.id}`}
              aria-selected={selected}
              aria-controls={`room-panel-${v.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(v.id)}
              className={cn(
                'relative inline-flex h-11 items-center whitespace-nowrap px-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                selected
                  ? 'font-semibold text-ink after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full after:bg-primary'
                  : 'font-medium text-ink-2 hover:text-ink',
              )}
            >
              {v.label}
            </button>
          );
        })}
      </div>
      <HelpTip id="buyer.room.views" className="shrink-0" />
    </div>
  );
}

export function RoomPage() {
  const access = useBuyerRoomAccess();
  if (access.status === 'login') return <Navigate to="/login" replace />;
  if (access.status === 'loading') {
    return (
      <p className="mx-auto w-full max-w-container px-4 py-6 md:px-6 text-sm text-muted-foreground">
        Loading…
      </p>
    );
  }
  if (access.status === 'forbidden') return <Navigate to="/" replace />;
  return <RoomPageInner roomId={access.roomId} />;
}

function RoomPageInner({ roomId }: { roomId: string }) {
  const funnel = useRoomFunnel(roomId);
  const documents = useRoomDocuments(roomId);
  const envelopes = useSigningEnvelopes(roomId);
  const buyerLoi = useBuyerLoi(roomId);
  // ADR-0046: another buyer's accepted LOI may pause this buyer's access.
  const exclusivity = useBuyerExclusivity(roomId);
  const paused = exclusivity.data?.paused === true;
  // Buyer Questions open exactly with the CIM (the server decides: `available`).
  const buyerQuestions = useBuyerQuestions(roomId);
  const [query, setQuery] = useState('');
  // The search box owns its keystrokes; a new key empties it ("Show all documents").
  const [searchKey, setSearchKey] = useState(0);
  const [view, setView] = useState<RoomView>('documents');
  // The Data Room's ONE results area (owner, 2026-10-15): the Ask answer or the
  // picked folder's documents — whichever the buyer did last. Asking clears the
  // folder and the search; picking a folder (or All documents) clears the answer.
  const [folderKey, setFolderKey] = useState<string | null>(null);
  const [periodKey, setPeriodKey] = useState<string | null>(null);
  const ask = useAskTheRoom(roomId, () => {
    setFolderKey(null);
    setPeriodKey(null);
    setQuery('');
  });
  const asking = ask.state.status !== 'idle';
  const pickFolder = (folder: string | null, period: string | null) => {
    // Picking a folder starts over: the question and its answer go (owner, 2026-10-22).
    ask.reset();
    setFolderKey(folder);
    setPeriodKey(period);
    // The first-run guide ticks "Browse by folder" once a folder has been picked.
    if (folder !== null) noteGuideActivity('folder');
  };
  const showAll = () => {
    ask.reset();
    setFolderKey(null);
    setPeriodKey(null);
    setQuery('');
    setSearchKey((k) => k + 1);
  };
  // Folders built from what the buyer can see — nothing locked or greyed for
  // areas outside their access (lib/folder-tree).
  const tree = useMemo(() => buildFolderTree(documents.data ?? []), [documents.data]);
  const column = useMemo(() => folderColumnView(tree), [tree]);
  const inFolder = documentsInFolder(tree, documents.data ?? [], folderKey, periodKey);
  // A folder that left the tree (access rolled back) falls back to All documents.
  useEffect(() => {
    if (folderKey !== null && inFolder === null) {
      setFolderKey(null);
      setPeriodKey(null);
    }
  }, [folderKey, inFolder]);
  // The document currently open in the protected viewer (issue #14), or null.
  const [activeDocument, setActiveDocument] = useState<RoomDocument | null>(null);
  // When set, the viewer fetches from this LOI-scoped content path instead of the
  // general document content endpoint (used for the buyer's own signed LOI).
  const [activeContentPath, setActiveContentPath] = useState<string | undefined>(undefined);

  // Open a document in the protected viewer. `contentPath` overrides the fetch
  // URL (the LOI-scoped read for the buyer's own LOI); omit it for ordinary
  // room documents, which go through the permission-scoped content endpoint.
  const openDocument = (doc: RoomDocument, contentPath?: string) => {
    // Nothing of a document being prepared can be shown yet (ADR-0064).
    if (doc.beingPrepared && contentPath === undefined) return;
    setActiveContentPath(contentPath);
    setActiveDocument(doc);
  };

  // "Find a document" over the funnel-scoped set (the API is the choke point,
  // so a hit can never fall outside what this stage allows).
  const searchParams = { query };
  const searchActive = hasSearchCriteria(searchParams);
  const search = useDocumentSearch(roomId, searchParams);

  // Any unauthenticated call bounces to the sign-in (password or emailed code).
  const unauthorized = [funnel.error, documents.error, envelopes.error, search.error].some(
    (e) => e instanceof ApiError && e.status === 401,
  );
  if (unauthorized) return <Navigate to="/login" replace />;

  if (funnel.isPending || documents.isPending || envelopes.isPending) {
    return (
      <div className="mx-auto w-full max-w-container px-4 py-6 md:px-6">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  const funnelState = funnel.data;
  const docs = documents.data ?? [];
  // The listing's server-computed `canDownload` per document. A search hit
  // carries none, so it takes the listing's flag by id (absent → view-only).
  // The content route still makes and audits the real decision on `?download=1`.
  const canDownloadById = new Map(docs.map((d) => [d.id, d.canDownload === true]));
  const searchDocs = (search.data ?? []).map((d) => ({
    ...d,
    canDownload: canDownloadById.get(d.id) ?? false,
  }));
  // The results area's folder view: the folder's documents, or the search hits in it.
  const folderIds = folderKey !== null ? new Set((inFolder ?? []).map((d) => d.id)) : null;
  const folderDocs = searchActive
    ? searchDocs.filter((d) => !folderIds || folderIds.has(d.id))
    : (inFolder ?? docs);
  const periodLabel = periodKey ? (describePeriodKey(periodKey)?.fullLabel ?? null) : null;
  const folderNames = folderKey !== null ? folderPathNames(tree, folderKey) : [];
  const folderLabel =
    folderKey !== null ? [...folderNames, ...(periodLabel ? [periodLabel] : [])].join(' › ') : '';
  const resultsTitle =
    ask.state.status !== 'idle'
      ? `Answer: ${ask.state.question}`
      : searchActive
        ? 'Search results'
        : folderKey !== null
          ? `Folder: ${folderLabel}`
          : 'All documents';
  // Never for an override content path (the LOI-scoped read is view-only).
  const activeCanDownload = activeContentPath === undefined && activeDocument?.canDownload === true;
  const envs = envelopes.data ?? [];

  const anyError = funnel.error ?? documents.error ?? envelopes.error;
  if (anyError || !funnelState) {
    return (
      <div className="mx-auto w-full max-w-container px-4 py-6 md:px-6">
        <ErrorText error={anyError ?? 'Unable to load the room.'} />
      </div>
    );
  }
  const stage = funnelState.stage;
  // A BUYER-RUN room (ADR-0039): this member is on the buyer's own team, so the
  // sell-side funnel — progress stepper, teaser/NDA/CIM copy, the LOI — does not
  // apply. The server says so on the caller's own funnel read.
  const buySide = funnelState.roomMode === 'buy_side';
  // Hidden while this buyer is paused by another buyer's exclusivity (ADR-0046;
  // the server also answers `available: false` then).
  const buyerQuestionsAvailable = buyerQuestions.data?.available === true && !paused;
  // A tab that closed (access rolled back) falls back to the documents.
  const shownView: RoomView =
    view === 'buyer-questions' && !buyerQuestionsAvailable ? 'documents' : view;

  // The admin-issued NDA/NC agreements the buyer signs to unlock the CIM. A
  // cancelled envelope is never shown as something to sign.
  const agreementEnvelopes = envs.filter(
    (e) => (e.envelopeType === 'nc' || e.envelopeType === 'nda') && !e.cancelledAt,
  );
  // Once every agreement is signed and sealed there is nothing left to sign:
  // the card says so instead of still asking for a typed signature.
  const allAgreementsSigned =
    agreementEnvelopes.length > 0 && agreementEnvelopes.every((e) => e.status === 'sealed');
  // What the LOI card shows — from the latest LOI and the stage (ADR-0029; a
  // countered or rejected round may upload a fresh LOI). See lib/loi-card.ts.
  const card = loiCard(stage, buyerLoi.data);

  return (
    <div className="mx-auto w-full max-w-container px-4 py-6 md:px-6 space-y-6">
      {/* First visit to the room: a short guide (hidden once done or dismissed). */}
      <BuyerGuide roomId={roomId} onOpenQuestions={() => setView('questions')} />
      {buySide ? null : (
        <Card>
          <CardHeader>
            <CardTitle>
              Your progress <HelpTip id="buyer.room.progress" className="ml-1" />
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Access opens up as you move through each step of the process.
            </p>
          </CardHeader>
          <CardContent>
            <FunnelStepper state={funnelState} />
          </CardContent>
        </Card>
      )}

      <BuyerExclusivityNotice
        view={exclusivity.data}
        titleAside={<HelpTip id="buyer.exclusivity.paused" />}
        clockLabelAside={<HelpTip id="buyer.exclusivity.clock" />}
      />

      {agreementEnvelopes.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>
              {allAgreementsSigned ? 'Your agreements' : 'Agreements to sign'}{' '}
              <HelpTip id="buyer.nda.agreements" className="ml-1" />
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {allAgreementsSigned
                ? 'You have signed every agreement for this room. A sealed copy is kept below.'
                : 'Read the agreement below, then type your name and sign it to unlock the confidential information memorandum (CIM).'}
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {agreementEnvelopes.map((envelope) => (
              // Read first, then type your name and Sign (the agreement is shown
              // in the view-only viewer; Sign is disabled until it has loaded).
              <AgreementSigningPanel key={envelope.id} roomId={roomId} envelope={envelope} />
            ))}
          </CardContent>
        </Card>
      ) : null}

      <RoomViewTabs
        view={shownView}
        views={ROOM_VIEWS.filter((v) => v.id !== 'buyer-questions' || buyerQuestionsAvailable)}
        onChange={setView}
      />
      {shownView === 'documents' ? (
        <div
          role="tabpanel"
          id="room-panel-documents"
          aria-labelledby="room-tab-documents"
          className="space-y-6"
        >
          {paused ? (
            <Card>
              <CardHeader>
                <CardTitle>Documents</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Documents are unavailable while data room access is paused.
                </p>
              </CardContent>
            </Card>
          ) : (
            // The folder column on the left; on the right "Ask the Data Room" and,
            // directly below it, the ONE results area (owner, 2026-10-15).
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
              <DataRoomFolders
                nodes={column.nodes}
                pending={false}
                selectedId={folderKey}
                selectedPeriod={periodKey}
                periodNodes={column.periodNodes}
                onSelect={(node) => pickFolder(node?.id ?? null, null)}
                onSelectPeriod={(node, key) => pickFolder(node.id, key)}
                label={folderKey !== null ? folderLabel : 'All documents'}
                headerAction={<HelpTip id="buyer.folders.tree" />}
              />
              <div className="min-w-0 space-y-3">
                {/* Ask the Data Room (ADR-0035): only documents this buyer may see can come back. */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle>
                      Ask the Data Room <HelpTip id="buyer.room.ask" className="ml-1" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <AskTheRoomPrompt ask={ask} />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <div className="flex items-start gap-1">
                      <ResultsTitle
                        title={resultsTitle}
                        onClear={asking || folderKey !== null || searchActive ? showAll : undefined}
                      />
                      <HelpTip id="buyer.folders.clear" className="mt-0.5 shrink-0" />
                    </div>
                    {asking ? (
                      <div className="flex items-start gap-2 pt-2">
                        <div className="min-w-0 flex-1" aria-live="polite">
                          <AskAnswerSummary state={ask.state} />
                        </div>
                        {ask.state.status === 'answered' &&
                        ask.state.answer.kind !== 'not_a_find_request' &&
                        ask.state.answer.results.length > 0 ? (
                          <HelpTip id="buyer.ask.answer" className="shrink-0" />
                        ) : null}
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-muted-foreground">
                          {buySide
                            ? 'The documents shared with your team.'
                            : stage === 'teaser'
                              ? 'Teaser materials. Sign the NDA to unlock the full CIM.'
                              : 'The documents available to you at this stage.'}{' '}
                          <HelpTip id="buyer.documents.badges" className="ml-0.5" />
                        </p>
                        <div className="flex items-center gap-2 pt-2">
                          <SearchBar
                            key={searchKey}
                            initialQuery={query}
                            onQueryChange={setQuery}
                            searching={search.isFetching}
                            className="min-w-0 flex-1"
                          />
                          <HelpTip id="buyer.search.box" className="shrink-0" />
                        </div>
                      </>
                    )}
                  </CardHeader>
                  <CardContent role="region" aria-label={`${resultsTitle}: document list`}>
                    {asking ? (
                      // An answer row is a listing row, carrying the server's own canDownload.
                      askAnswerDocuments(ask.state).length > 0 ? (
                        <DocumentList
                          documents={askAnswerDocuments(ask.state)}
                          onOpen={(doc) => openDocument(doc)}
                        />
                      ) : null
                    ) : searchActive && search.isPending ? (
                      <p className="text-sm text-muted-foreground">Loading…</p>
                    ) : searchActive && search.error ? (
                      <ErrorText error={search.error} />
                    ) : (
                      <DocumentList
                        documents={folderDocs}
                        onOpen={(doc) => openDocument(doc)}
                        emptyMessage={
                          searchActive
                            ? 'No documents match your search.'
                            : 'No documents are available to you yet.'
                        }
                      />
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
          {/* ADR-0056: the buyer's own daily new-document email. */}
          <NewDocumentAlertsToggle roomId={roomId} />
        </div>
      ) : shownView === 'buyer-questions' ? (
        <div
          role="tabpanel"
          id="room-panel-buyer-questions"
          aria-labelledby="room-tab-buyer-questions"
        >
          <Card>
            <CardHeader>
              <CardTitle>
                Buyer Questions <HelpTip id="buyer.buyer-questions.tab" className="ml-1" />
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Questions buyers have asked, answered and shared by the deal team.
              </p>
            </CardHeader>
            <CardContent>
              <PublishedQuestionList
                questions={buyerQuestions.data?.questions ?? []}
                onOpen={(ref) => {
                  const doc = docs.find((d) => d.id === ref.id);
                  if (doc) openDocument(doc);
                }}
                emptyMessage="No questions have been shared yet."
              />
            </CardContent>
          </Card>
        </div>
      ) : (
        <div role="tabpanel" id="room-panel-questions" aria-labelledby="room-tab-questions">
          <Card>
            <CardHeader>
              <CardTitle>
                Questions <HelpTip id="buyer.questions.form" className="ml-1" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <QuestionsPanel
                roomId={roomId}
                documents={docs}
                onOpen={(doc) => openDocument(doc)}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* The LOI belongs to the sell-side funnel: not for a buy-side team. */}
      {buySide ? null : (
        <Card>
          <CardHeader>
            <CardTitle>
              Letter of intent <HelpTip id="buyer.loi.card" className="ml-1" />
            </CardTitle>
            {card.kind === 'upload' ? (
              <p className="text-sm text-muted-foreground">
                Submit your signed LOI: upload the letter of intent you have signed. The seller will
                accept, counter, or reject it here.
              </p>
            ) : null}
          </CardHeader>
          <CardContent className="space-y-3">
            {card.kind === 'status' ? (
              <div className="space-y-2 rounded-md border p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <LoiStatusChip status={card.loi.status} />
                  <HelpTip id="buyer.loi.status" />
                  <span className="text-xs text-muted-foreground">
                    Submitted {formatDate(card.loi.submittedAt)}
                    {card.loi.decidedAt
                      ? ` · ${LOI_STATUS_LABELS[card.loi.status]} ${formatDate(card.loi.decidedAt)}`
                      : ''}
                  </span>
                </div>
                <p className="text-sm">{LOI_STATUS_MESSAGES[card.loi.status]}</p>
                {card.loi.documentId ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      openDocument(
                        loiDocument(roomId, card.loi),
                        loiDocumentContentPath(roomId, card.loi.id),
                      )
                    }
                  >
                    View your signed LOI
                  </Button>
                ) : null}
              </div>
            ) : card.kind === 'upload' ? (
              <LoiForm roomId={roomId} />
            ) : card.kind === 'past' ? (
              <p className="text-sm font-medium text-st-certified-fg">
                Your letter of intent is on file. The deal team will be in touch.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Available once you have signed the NDA and the CIM is unlocked.
              </p>
            )}
            {card.kind === 'status' && card.canResubmit ? (
              <div className="space-y-2 border-t pt-3">
                <p className="text-sm font-medium">Submit a revised signed LOI</p>
                <LoiForm roomId={roomId} />
              </div>
            ) : null}
          </CardContent>
        </Card>
      )}

      {activeDocument ? (
        <DocumentViewer
          roomId={roomId}
          document={activeDocument}
          viewerIdentity={getViewerIdentity()}
          contentPath={activeContentPath}
          canDownload={activeCanDownload}
          onClose={() => {
            setActiveDocument(null);
            setActiveContentPath(undefined);
          }}
        />
      ) : null}
    </div>
  );
}
